import type { Booking, BookingGarment, BookingWearer } from '../api/bookings';

/** A garment with work outstanding, paired with whoever is going to wear it. */
export interface DocketLine {
  garment: BookingGarment;
  wearer: BookingWearer;
}

/**
 * Every garment on a booking that has alteration work on it, in the order the booking lists them.
 *
 * <p>
 * Separate from the component that renders the docket so that a caller can ask the question without
 * rendering a sheet to find the answer — a booking with nothing to alter should not offer a
 * "print docket" button at all, rather than printing a page with a heading and no work on it.
 * </p>
 *
 * <p>
 * <strong>The pairing is the point.</strong> The alteration belongs to the garment and the
 * measurements belong to the person, so a docket line needs both: a tailor shortening a sleeve is
 * doing it to fit Nilesh, and the fitting date that justifies the numbers is on his record, not on
 * the tuxedo's.
 * </p>
 */
export function docketLines(booking: Booking): DocketLine[] {
  return booking.wearers.flatMap((wearer) =>
    wearer.garments
      .filter((garment) => garment.alterations.length > 0)
      .map((garment) => ({ garment, wearer })),
  );
}

/**
 * The one garment's line, or none.
 *
 * <p>
 * <strong>A docket is a sheet that travels with a garment</strong>, so one garment is the unit a
 * workshop actually works in: two garments off one booking go to two tailors, on two due dates, and
 * come back separately. Printing the booking's whole list on one page — which is what this module
 * did at first — gave a sheet headed with every tailor's name at once and the earliest of the due
 * dates, which is a date that is wrong for all but one of them.
 * </p>
 *
 * <p>
 * Filtered the same way {@link docketLines} is: a garment with no measurements on it has nothing to
 * print, whatever its state or its job number say.
 * </p>
 */
export function docketLinesFor(booking: Booking, garmentId: number): DocketLine[] {
  return docketLines(booking).filter((line) => line.garment.id === garmentId);
}
